/**
 * WhatsApp Templates TanStack Query Hooks
 *
 * React Query hooks for WhatsApp template management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waApi,
  type WATemplatesListParams,
  type WATemplatesListResponse,
  type WATemplateStats,
  type WATemplateDetail,
  type WATemplateSyncResponse,
  type WATemplate,
  type WATemplateUpdateInput,
  type WATemplateCreateInput,
  type WATemplatePreviewResponse,
} from '@/lib/api-client'

// Query keys
export const waTemplatesKeys = {
  all: ['wa-templates'] as const,
  lists: () => [...waTemplatesKeys.all, 'list'] as const,
  list: (params?: WATemplatesListParams) => [...waTemplatesKeys.lists(), params] as const,
  stats: () => [...waTemplatesKeys.all, 'stats'] as const,
  details: () => [...waTemplatesKeys.all, 'detail'] as const,
  detail: (id: number) => [...waTemplatesKeys.details(), id] as const,
  previews: () => [...waTemplatesKeys.all, 'preview'] as const,
  preview: (id: number, patientId?: number) => [...waTemplatesKeys.previews(), id, patientId] as const,
}

/**
 * Hook to fetch list of WhatsApp templates
 */
export function useWATemplates(params?: WATemplatesListParams) {
  return useQuery<WATemplatesListResponse>({
    queryKey: waTemplatesKeys.list(params),
    queryFn: () => waApi.templates.list(params),
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Hook to fetch WhatsApp template statistics
 */
export function useWATemplateStats() {
  return useQuery<WATemplateStats>({
    queryKey: waTemplatesKeys.stats(),
    queryFn: () => waApi.templates.stats(),
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook to fetch single WhatsApp template details
 */
export function useWATemplate(id: number | null) {
  return useQuery<WATemplateDetail>({
    queryKey: waTemplatesKeys.detail(id ?? 0),
    queryFn: () => waApi.templates.get(id!),
    enabled: !!id,
  })
}

/**
 * Hook to fetch template preview with patient data
 */
export function useWATemplatePreview(id: number | null, patientId?: number) {
  return useQuery<WATemplatePreviewResponse>({
    queryKey: waTemplatesKeys.preview(id ?? 0, patientId),
    queryFn: () => waApi.templates.preview(id!, patientId),
    enabled: !!id,
  })
}

/**
 * Hook to create a new template on Meta
 */
export function useCreateWATemplate() {
  const queryClient = useQueryClient()

  return useMutation<WATemplate, Error, WATemplateCreateInput>({
    mutationFn: (data) => waApi.templates.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waTemplatesKeys.all })
    },
  })
}

/**
 * Hook to sync templates from Meta
 */
export function useSyncWATemplates() {
  const queryClient = useQueryClient()

  return useMutation<WATemplateSyncResponse, Error>({
    mutationFn: () => waApi.templates.sync(),
    onSuccess: () => {
      // Invalidate all template queries after sync
      queryClient.invalidateQueries({ queryKey: waTemplatesKeys.all })
    },
  })
}

/**
 * Hook to update a template (variable mapping)
 */
export function useUpdateWATemplate() {
  const queryClient = useQueryClient()

  return useMutation<WATemplate, Error, { id: number; data: WATemplateUpdateInput }>({
    mutationFn: ({ id, data }) => waApi.templates.update(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific template and list
      queryClient.invalidateQueries({ queryKey: waTemplatesKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: waTemplatesKeys.lists() })
    },
  })
}

/**
 * Hook to delete a template
 */
export function useDeleteWATemplate() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; message: string; deletedId: number }, Error, number>({
    mutationFn: (id) => waApi.templates.delete(id),
    onSuccess: () => {
      // Invalidate all template queries after delete
      queryClient.invalidateQueries({ queryKey: waTemplatesKeys.all })
    },
  })
}
